import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';
export type NotificationCategory = 'booking' | 'payment' | 'system' | 'hotel' | 'review' | 'security';

export interface NotificationData {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  is_read: boolean;
  created_at: string;
  category: NotificationCategory;
  priority?: number;
}

export interface CreateNotificationData {
  title: string;
  message: string;
  type: NotificationType;
  category: NotificationCategory;
  priority?: number;
  hotel_id: string;
  user_id?: string;
}

export const useNotifications = (limit: number = 10) => {
  const queryClient = useQueryClient();
  const { currentHotel } = useOptimizedHotel();
  const hotelId = currentHotel?.id;

  const query = useQuery({
    queryKey: ['notifications', limit, hotelId],
    queryFn: async (): Promise<NotificationData[]> => {
      if (!hotelId) return [];

      // 🎯 AK-47: hotel_id фильтр для tenant-изоляции
      const { data, error } = await supabase
        .from('notifications')
        .select('id, title, message, type, is_read, created_at, category, priority')
        .eq('hotel_id', hotelId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    },
    enabled: !!hotelId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!hotelId) return;
      // 🎯 AK-47: hotel_id фильтр при массовом обновлении
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('is_read', false)
        .eq('hotel_id', hotelId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const createNotificationMutation = useMutation({
    mutationFn: async (notificationData: CreateNotificationData) => {
      const { error } = await supabase
        .from('notifications')
        .insert({
          title: notificationData.title,
          message: notificationData.message,
          type: notificationData.type,
          category: notificationData.category,
          priority: notificationData.priority || 1,
          hotel_id: notificationData.hotel_id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const notifications = query.data || [];
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const markAsRead = (notificationId: string) => {
    markAsReadMutation.mutate(notificationId);
  };

  const markAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const createNotification = (notificationData: CreateNotificationData) => {
    createNotificationMutation.mutate(notificationData);
  };

  return {
    data: query.data,
    notifications,
    unreadCount,
    loading: query.isLoading,
    error: query.error,
    markAsRead,
    markAllAsRead,
    createNotification,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isCreating: createNotificationMutation.isPending,
    ...query
  };
};
